'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import StudentAuthDialog from './StudentAuthDialog';
import { getStudentMe, loginStudent, signupStudent } from '@/lib/lms/api';
import type { StudentSession } from '@/lib/lms/types';
import { readStudentSession, writeStudentSession } from '@/lib/lms/utils';

type AuthMode = 'login' | 'signup';

interface AuthDialogState {
  open: boolean;
  mode: AuthMode;
  message?: string;
}

interface StudentSessionContextValue {
  session: StudentSession | null;
  isReady: boolean;
  isAuthenticating: boolean;
  openAuthDialog: (mode?: AuthMode, message?: string) => void;
  closeAuthDialog: () => void;
  login: (payload: { email: string; password: string }) => Promise<void>;
  signup: (payload: { name: string; email: string; phone: string; password: string }) => Promise<void>;
  applyAuthSession: (authSession: StudentSession) => Promise<void>;
  logout: () => void;
  refreshSession: () => Promise<StudentSession | null>;
}

const StudentSessionContext = createContext<StudentSessionContextValue | null>(null);

export function StudentSessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<StudentSession | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [dialogState, setDialogState] = useState<AuthDialogState>({
    open: false,
    mode: 'login',
  });

  const refreshSession = async () => {
    const storedSession = readStudentSession();
    if (!storedSession?.token) {
      setSession(null);
      writeStudentSession(null);
      return null;
    }

    try {
      const response = await getStudentMe(storedSession.token);
      if (response.user.role !== 'USER') {
        setSession(null);
        writeStudentSession(null);
        return null;
      }
      const nextSession = {
        token: storedSession.token,
        user: response.user,
        meta: response.meta,
      };

      setSession(nextSession);
      writeStudentSession(nextSession);
      return nextSession;
    } catch {
      setSession(null);
      writeStudentSession(null);
      return null;
    }
  };

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      const storedSession = readStudentSession();
      if (storedSession) {
        setSession(storedSession);
      }

      await refreshSession();

      if (active) {
        setIsReady(true);
      }
    };

    void bootstrap();

    return () => {
      active = false;
    };
  }, []);

  const persistAuthResponse = async (authSession: StudentSession) => {
    writeStudentSession(authSession);
    setSession(authSession);

    try {
      const response = await getStudentMe(authSession.token);
      const nextSession = {
        token: authSession.token,
        user: response.user,
        meta: response.meta,
      };

      writeStudentSession(nextSession);
      setSession(nextSession);
    } catch {
      writeStudentSession(authSession);
      setSession(authSession);
    }
  };

  const handleLogin = async (payload: { email: string; password: string }) => {
    setIsAuthenticating(true);

    try {
      const response = await loginStudent(payload);
      if (response.user.role !== 'USER') {
        throw new Error('Student storefront login is only for learner accounts. Admin and instructor accounts should use /admin.');
      }
      await persistAuthResponse({
        token: response.token,
        user: response.user,
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSignup = async (payload: { name: string; email: string; phone: string; password: string }) => {
    setIsAuthenticating(true);

    try {
      const response = await signupStudent(payload);
      await persistAuthResponse({
        token: response.token,
        user: response.user,
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const value = useMemo<StudentSessionContextValue>(
    () => ({
      session,
      isReady,
      isAuthenticating,
      openAuthDialog: (mode = 'login', message) =>
        setDialogState({
          open: true,
          mode,
          message,
        }),
      closeAuthDialog: () =>
        setDialogState((current) => ({
          ...current,
          open: false,
        })),
      login: handleLogin,
      signup: handleSignup,
      applyAuthSession: persistAuthResponse,
      logout: () => {
        setSession(null);
        writeStudentSession(null);
      },
      refreshSession,
    }),
    [isAuthenticating, isReady, session],
  );

  return (
    <StudentSessionContext.Provider data-component-id="src/components/lms/StudentSessionProvider.tsx:175:4" data-component-path="src/components/lms/StudentSessionProvider.tsx" data-component-line="175" data-component-end-line="196" data-component-file="StudentSessionProvider.tsx" data-component-name="StudentSessionContext.Provider" data-component-content="%7B%22elementName%22%3A%22StudentSessionContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>
      {children}
      <StudentAuthDialog data-component-id="src/components/lms/StudentSessionProvider.tsx:177:6" data-component-path="src/components/lms/StudentSessionProvider.tsx" data-component-line="177" data-component-end-line="195" data-component-file="StudentSessionProvider.tsx" data-component-name="StudentAuthDialog" data-component-content="%7B%22elementName%22%3A%22StudentAuthDialog%22%7D"
        open={dialogState.open}
        mode={dialogState.mode}
        message={dialogState.message}
        onClose={() =>
          setDialogState((current) => ({
            ...current,
            open: false,
          }))
        }
        onModeChange={(mode) =>
          setDialogState((current) => ({
            ...current,
            mode,
          }))
        }
        onLogin={handleLogin}
        onSignup={handleSignup}
      />
    </StudentSessionContext.Provider>
  );
}

export const useStudentSession = () => {
  const context = useContext(StudentSessionContext);
  if (!context) {
    throw new Error('useStudentSession must be used inside StudentSessionProvider.');
  }

  return context;
};
